// import rules from '@/helpers/validation-rules';
// import store from '@/store';
import firebase from 'firebase/app';
import 'firebase/auth';
import axios from '@/helpers/axios-config';
import getIdToken from '@/helpers/firebase-token';
import { LOCAL_STORAGE_PROMO_POPUP_COUNTER, LOCAL_STORAGE_RAF_POPUP_COUNTER } from '@/helpers/constants';
import { AUTH_PROVIDERS, SIGN_UP_STEPS } from '@/helpers/enums';
import websocket from '@/services/websocket';
import utmUtils from '@/helpers/utm-utils';

const API_URL = process.env.VUE_APP_API_URL;
const FB_GRAPH_URL = process.env.VUE_APP_FB_GRAPH_URL;

function handleAuthErrors(errorCode) {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'Incorrect email or password!';
    case 'auth/wrong-password':
      return 'Incorrect email or password!';
    case 'auth/too-many-requests':
      // it's necessary to get precise business logic for further account lock!
      return 'Multiple failed login attempts detected!';
    case 'auth/user-disabled':
      return "Your account is banned. Please contact <a href='/contact' class='gold--text'>Customer Support</a>.";
    default:
      return 'An error occurred. Please try again later.';
  }
}

function getAuthProvider(providerName) {
  if (providerName === AUTH_PROVIDERS.GOOGLE) return new firebase.auth.GoogleAuthProvider();
  if (providerName === AUTH_PROVIDERS.FACEBOOK) return new firebase.auth.FacebookAuthProvider();
  return null;
}

export default {
  state: {
    accountCreated: false,
    loggedInUser: localStorage.getItem('userInfo') != null ? JSON.parse(localStorage.getItem('userInfo')) : null,
    error: null,
    idToken: '',
    session: null,

    loading: false,
    showLoginDialog: false,
    showSignupDialog: false,
    showResetPasswordDialog: false,
    partnerId: null,
    signupStep: SIGN_UP_STEPS.CREDENTIALS,
    firstSignin: false,
  },

  mutations: {
    setAccountCreated(state, payload) {
      state.accountCreated = payload;
    },
    setPartnerId(state, payload) {
      state.partnerId = payload;
    },
    setUserIDToken(state, payload) {
      state.idToken = payload;
    },
    setLogout(state) {
      state.loggedInUser = null;
      state.loading = false;
      state.error = null;
      state.isLoggedIn = false;

      localStorage.removeItem('user');
      localStorage.removeItem('session');
    },
    setLoading(state, data) {
      state.loading = data;
      state.error = null;
    },
    setError(state, data) {
      state.error = data;
      state.loggedInUser = null;
      state.loading = false;
    },
    clearError(state) {
      state.error = null;
    },

    setLoginDialogState(state, data) {
      state.showLoginDialog = data;
    },
    setSignupDialogState(state, data) {
      // TODO: this is a dirty hack to prevent accidental closing on step 2 (phone verification)
      if (state.signupStep === SIGN_UP_STEPS.VERIFY_PHONE && data === false) {
        return;
      }

      if (state.signupStep === SIGN_UP_STEPS.REDIRECT_TO_ONE && data === false) {
        state.signupStep = 1;
      }

      if (state.signupStep === SIGN_UP_STEPS.OTP_VERIFICATION && data === false) {
        return;
      }

      state.showSignupDialog = data;
    },
    setResetPasswordDialogState(state, data) {
      state.showResetPasswordDialog = data;
    },
    setSignupStep(state, data) {
      state.signupStep = data;
    },
    setFirstSignIn(state, data) {
      state.firstSignin = data;
    },
  },

  actions: {
    async getUserProfile({ commit, dispatch }) {
      commit('setLoading', false);
      const uid = await firebase.auth().currentUser.uid;

      await firebase
        .database()
        .ref('users')
        .child(uid)
        .once('value')
        .then(async (data) => {
          if (data.exists()) {
            let profile = data.val();
            await dispatch('setLoggedIn', true);
            commit('setUser', profile);
          }
          commit('setLoading', false);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    changeFirstSignInState({ commit }, data) {
      commit('setFirstSignIn', data);
    },

    changeSignupStep({ commit }, data) {
      commit('setSignupStep', data);
    },

    changeLoginDialogState({ commit, dispatch }, data) {
      if (data) {
        dispatch('hideAlertDialog');
      }
      commit('setLoginDialogState', data);
    },

    changeSignupDialogState({ commit, dispatch }, data) {
      if (data) {
        dispatch('hideAlertDialog');
      }
      commit('setSignupDialogState', data);
    },

    changeResetPasswordDialogState({ commit }, data) {
      commit('setResetPasswordDialogState', data.value);
    },

    async autoLogin({ commit, dispatch, getters }) {
      commit('clearError');
      commit('setLoading', true);

      const userKey = firebase.auth()?.currentUser?.uid;
      if (!userKey) return;

      // todo: replace with our API
      //await dispatch('getUserProfile');
      //const newUser = getters.getuser;
      await dispatch('getPlayerInfo');
      const newUser = getters.getPlayerInfo?.profile;

      if (!newUser) return;

      newUser.id = userKey;
      newUser.uid = userKey;
      getIdToken({ forceLoad: true })
        .then((idToken) => {
          commit('setUserIDToken', idToken);
        })
        .catch((error) => {
          console.error('error is ', error);
        });

      commit('setUser', newUser);
    },

    login({ commit, dispatch, getters }, data) {
      commit('clearError');
      commit('setLoading', true);
      firebase
        .auth()
        .signInWithEmailAndPassword(data.email, data.password)
        .then(async (user) => {
          // todo: replace with our API
          //await dispatch('getUserProfile');
          //const newUser = getters.getuser;
          await dispatch('getPlayerInfo');
          const newUser = getters.getPlayerInfo?.profile;

          newUser.id = user?.user?.uid || null;
          newUser.uid = user?.user?.uid || null;

          getIdToken({ forceLoad: true })
            .then((idToken) => {
              commit('setUserIDToken', idToken);
            })
            .catch((error) => {
              console.error('error is ', error);
            });
          commit('setUser', newUser);
        })
        .catch(function (error) {
          console.error(error);
          localStorage.removeItem('userInfo');
          commit('setError', error);
        });
    },

    async signUserUp({ commit, state, dispatch }, payload) {
      commit('setLoading', true);

      const data = {
        displayName: payload.displayName,
        email: payload.email,
        checkAds: payload.checkAds,
        check18: payload.check18,
        partnerId: state.partnerId,
        googleToken: payload.googleToken,
        state: payload.state,
        dateofbirth: payload.dateofbirth,
        ...utmUtils.getUTMParams(),
      };

      if (payload.viaProvider) {
        data.viaProvider = true;
      } else if (payload.password) {
        data.password = payload.password;
      }

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${API_URL}/createUser`,
        headers: { 'Content-Type': 'application/json' },
        data,
      };

      try {
        const { data } = await axios.request(config);

        if (data.haserror) {
          dispatch('showAlertDialog', {
            title: 'Account not created',
            text: data.status,
          });
          throw new Error('Account not created');
        } else {
          commit('setAccountCreated', true);

          utmUtils.removeUTMParamsFromLocalStorage();

          if (payload.viaProvider) {
            if (data.user) {
              commit('setUser', data.user);
            }

            await dispatch('setLoggedIn', false);

            if (data.user.displayName) {
              if (data.user.id) {
                dispatch('setDatabaseListeners', { userId: data.user.id, displayName: data.user.displayName });
              }
            }

            commit('SET_SOCIAL_SIGN_UP', false);
            dispatch('changeSignupStep', SIGN_UP_STEPS.VERIFY_PHONE);
          } else {
            dispatch('changeSignupStep', SIGN_UP_STEPS.EMAIL_VERIFICATION);
          }

          if (data.opened) {
            dispatch('changeSignupStep', SIGN_UP_STEPS.CONGRATS);
          }

          // New changhes here !
          // store.dispatch("showAlertDialog", {
          //   icon: "/images/icons/mail.png",
          //   title: "Your account was created",
          //   text: `We've sent a confirmation mail to ${payload.email}. Please click the link in the mail to verify your account and start playing!`,
          // });
        }
      } catch (error) {
        console.error(error);

        await dispatch('signOut');

        if (error.response?.data?.status === 'This Display Name is taken, please select another') {
          dispatch('showAlertDialog', {
            title: 'Username already taken.',
            text: 'Please choose a different username.',
          });
        }

        if (error.response?.data?.status === 'User exists already') {
          dispatch('showAlertDialog', {
            title: 'This email address is already registered.',
            text: 'Please log in with your existing account.',
          });
        }
      } finally {
        commit('setLoading', false);
      }
    },

    setDatabaseListeners(store, payload) {
      let displayName = payload.displayName;
      const db = firebase.database();
      const userStatusRef = db.ref(`player_displaynames/${displayName}/online`);

      const connectedRef = db.ref('.info/connected');
      connectedRef?.on('value', async (snapshot) => {
        if (snapshot.val() === false) return;

        userStatusRef?.onDisconnect().set(false);
        websocket.emit('playing', { isPlaying: false });

        await userStatusRef?.set(true);

        if (this.$store.getters.getIFrameDialog) {
          websocket.emit('playing', { isPlaying: true });
        }
      });
    },

    async processSocialSignUp({ dispatch, getters }, firebaseUser) {
      // get user from the firebase
      // await dispatch('getUserProfile');
      // const authUser = getters.getuser;
      // todo: replace with our API
      if (!localStorage.getItem('session')) {
        await dispatch('setLoggedIn', false);
      }

      await dispatch('getPlayerInfo');
      const authUser = getters.getPlayerInfo?.profile;

      if (authUser) {
        const emailResponse = await dispatch('checkEmailAvailability', authUser.email);
        const userExists = emailResponse?.data?.hasError;

        if (userExists) {
          // user exists, log them in
          dispatch('changeSignupDialogState', false);
          dispatch('changeLoginDialogState', false);
          return { status: 'success', userExists: true, user: authUser };
        } else {
          // we want to use this users' data to finish the registration process
          return { status: 'success', user: authUser };
        }
      } else if (firebaseUser) {
        return { status: 'success', user: firebaseUser };
      }

      await firebase.auth().signOut();
      return { status: 'error', reason: 'no_such_firebase_user' };
    },

    async getFacebookData({ dispatch }, { credential, email }) {
      const response = await fetch(`${FB_GRAPH_URL}/me?access_token=${credential.accessToken}`);
      const userdata = await response.json();
      await dispatch('linkSocialSignIn', {
        email: email,
        uid: userdata.id,
        providerId: credential.providerId,
      });

      await firebase.auth().signInWithCredential(credential);
      return await dispatch('processSocialSignUp');
    },

    async socialSignin({ dispatch, commit }, providerName) {
      const provider = getAuthProvider(providerName);

      commit('setIsManualSignIn', true);

      if (!provider) {
        return { status: 'error', reason: 'invalid_provider' };
      }

      try {
        const { user } = await firebase.auth().signInWithPopup(provider);
        return await dispatch('processSocialSignUp', user);
      } catch (error) {
        console.error(error);

        try {
          if (error.code === 'auth/account-exists-with-different-credential') {
            const credential = error.credential;
            const email = error.email;

            if (credential.providerId === 'facebook.com') {
              return await dispatch('getFacebookData', { credential, email });
            }

            console.error(`Error signing up with ${provider}:`, error);

            return { status: 'error' };
          }

          return { status: 'error', reason: 'unhandled_error' };
        } catch (err) {
          console.error(err);
          return { status: 'error' };
        }
      }
    },

    async linkSocialSignIn({ commit, dispatch }, payload) {
      const email = payload.email;
      const uid = payload.uid;
      const providerId = payload.providerId;

      let data = JSON.stringify({
        email: email,
        uid: uid,
        providerId: providerId,
      });

      let config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: API_URL + '/linkSocialSignIn',
        headers: {
          MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
          'Content-Type': 'application/json',
        },
        data: data,
      };
      await axios
        .request(config)
        .then((response) => {
          dispatch('changeSignupDialogState', false);
          dispatch('changeLoginDialogState', false);
          commit('setUser', response.data.user);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    async signUserIn({ getters, commit, dispatch }, payload) {
      commit('clearError');
      commit('clearLoginResponse');
      commit('setLoading', true);
      commit('setIsManualSignIn', true);

      try {
        let user;

        if (payload.isSocialSignIn) {
          user = getters.getuser;
        } else {
          const firebaseUser = await firebase.auth().signInWithEmailAndPassword(payload.email, payload.password);
          user = firebaseUser?.user;
        }

        if (user?.emailVerified || payload.isSocialSignIn) {
          const key = firebase.auth().currentUser.uid;

          // todo: replace with our API
          // await dispatch('getUserProfile');
          // const user = getters.getuser;
          await dispatch('getPlayerInfo');
          const user = getters.getPlayerInfo?.profile;

          if (!user) {
            const error = new Error('Empty user profile during login process');
            error.code = 'USER_PROFILE_EMPTY'; // Custom error code
            throw error;
          }

          if (user.status === 'closed') {
            await dispatch('showAlertDialog', {
              title: 'Your account is closed.',
              text: "Your account is temporarily closed. Please contact <a href='/contact' class='gold--text'>Customer Support</a>.",
            });
            commit('setLoading', false);
            return { error: 'suspended' };
          }

          if (user.disabled) {
            dispatch('showAlertDialog', {
              title: 'Your account is banned.',
              text: "Your account is permanently banned. Please contact <a href='/contact' class='gold--text'>Customer Support</a>.",
              permanent: true,
            });
            dispatch('finishGameSession', { sid: null, privateRoomId: null });
            dispatch('hideIFrameDialog', true);
            await dispatch('signUserOut');

            commit('setLoading', false);
            return { error: 'banned' };
          } else {
            //await dispatch('setLoggedIn', false); // TODO: maybe this is not needed
            user.id = key;
            commit('setUser', user);

            commit('setLoading', false);

            await dispatch('getActiveGames');

            commit('setIsManualSignIn', false);
            commit('setLoading', false);
          }

          dispatch('changeLoginDialogState', false);
        } else {
          dispatch('showConfirmDialog', {
            title: '',
            text: 'Your e-mail has not been verified',
            noVisible: false,
            yesText: 'Verify',
            callback: async () => {
              const config = {
                method: 'post',
                url: API_URL + '/resendVerificationCode',
                headers: {
                  MQTECH_REQUEST_KEY: process.env.VUE_APP_MQ_TECH_API_KEY,
                  'Content-Type': 'application/json',
                },
                data: { email: payload.email },
              };

              try {
                const response = await axios.request(config);

                if (!response?.data) throw new Error('resendVerificationCode error');

                if (response.data.haserror) {
                  dispatch('showAlertDialog', {
                    title: '',
                    text: response.data.message,
                  });
                } else {
                  dispatch('showAlertDialog', {
                    title: response.data.message,
                    text: 'Check your mail, please!',
                  });
                }
              } catch (error) {
                dispatch('showAlertDialog', {
                  title: 'Something went wrong',
                  text: error.response.data.message,
                });
              }
            },
          });

          commit('setIsManualSignIn', false);
          commit('setLoading', false);
        }
      } catch (error) {
        commit('setError', error);
        // skip error handling for empty user profile
        if (error?.code !== 'USER_PROFILE_EMPTY') {
          commit('setLoginError', handleAuthErrors(error.code));
        }
        commit('setIsManualSignIn', false);
      } finally {
        commit('setLoading', false);
      }
    },

    signOut({ commit }) {
      firebase
        .auth()
        .signOut()
        .then(
          () => {
            localStorage.removeItem('userInfo');
            localStorage.removeItem(LOCAL_STORAGE_PROMO_POPUP_COUNTER);
            localStorage.removeItem(LOCAL_STORAGE_RAF_POPUP_COUNTER);
            commit('setLogout');
          },
          () => {},
        );
    },

    async handleExpiredSession({ dispatch }) {
      dispatch('signUserOut');
      dispatch('showAlertDialog', {
        title: 'Logged Out',
        text: 'You have been logged out due to login on another device.',
        permanent: true,
      });
    },

    async handleRestrictedAccess({ dispatch }, payload) {
      dispatch('signUserOut');
      dispatch('showAlertDialog', {
        title: 'Access forbidden',
        subTitle: 'You are accessing from restricted country/state',
        text: payload?.error || '',
      });
    },

    async handleUnauthorizedAccess({ dispatch }, payload) {
      dispatch('signUserOut');
      dispatch('showAlertDialog', {
        title: 'Access restricted',
        subTitle: 'Your access has been restricted',
        text: payload?.error || '',
      });
    },

    async handleUserIsInGameAlready({ dispatch }) {
      dispatch('signUserOut');
      dispatch('showAlertDialog', {
        title: 'Logged Out',
        text: 'You are already in a game.',
        permanent: true,
      });
    },

    async callTrafficTracker(_, payload) {
      if (typeof payload !== 'object' || !Object.keys(payload).length) return;

      return axios.request({
        method: 'post',
        url: `${API_URL}/traffic_tracker`,
        headers: { 'Content-Type': 'application/json' },
        data: payload,
      });
    },
  },

  getters: {
    accountCreated: (state) => state.accountCreated,
    loggedInUser: (state) => state.loggedInUser,
    getIdToken: (state) => state.idToken,
    showLoginDialog: ({ showLoginDialog }) => showLoginDialog,
    showSignupDialog: ({ showSignupDialog }) => showSignupDialog,
    showResetPasswordDialog: ({ showResetPasswordDialog }) => showResetPasswordDialog,
    getSignUpStep: (state) => state.signupStep,
    getPartnerId(state) {
      return state.partnerId;
    },
  },
};
